import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Seo from "@/components/Seo";
import { MaskedLine, FadeIn } from "@/components/Reveal";
import { GALLERY_ITEMS, GALLERY_CATEGORIES } from "@/data/content";

export default function Gallery() {
  const [active, setActive] = useState("Tutte");
  const items =
    active === "Tutte"
      ? GALLERY_ITEMS
      : GALLERY_ITEMS.filter((i) => i.category === active);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Gallery | Laure Hila — Viaggia in Grande"
        description="Viaggi, eventi, il mio percorso e le persone che ne fanno parte: la gallery di Laure Hila e Viaggia in Grande."
      />

      <section className="pt-32 md:pt-44 pb-10 md:pb-14 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Gallery</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Momenti <span className="italic text-gold">veri</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.4}>
            <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              Viaggi, eventi, il mio percorso e le persone che lo rendono speciale.
              Solo momenti reali, senza pose costruite.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div
            className="flex flex-wrap gap-2.5 mb-10 md:mb-14"
            role="tablist"
            aria-label="Categorie gallery"
          >
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={active === c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  active === c
                    ? "bg-ink text-cream"
                    : "bg-sand text-stone hover:text-gold border border-line"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <AnimatePresence mode="popLayout">
              {items.map((item, i) => (
                <motion.figure
                  layout
                  key={`${item.category}-${item.label}-${i}`}
                  data-testid={`gallery-item-${i}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className={`group relative overflow-hidden rounded-xl ${
                    i % 5 === 0 ? "row-span-2" : ""
                  }`}
                >
                  <img
                    src={item.image.url}
                    alt={item.image.alt}
                    style={item.pos ? { objectPosition: item.pos } : undefined}
                    className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out ${
                      i % 5 === 0 ? "aspect-[3/4] h-full" : "aspect-square"
                    }`}
                    loading="lazy"
                  />
                  <figcaption className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
                        {item.category}
                      </p>
                      <p className="font-serif text-lg text-cream mt-1">{item.label}</p>
                    </div>
                  </figcaption>
                </motion.figure>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
